/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Typography,
  IconButton,
  Card,
  CardContent,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Stack,
  Divider,
  Alert,
  OutlinedInput,
  Checkbox,
  ListItemText,
  useTheme
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import useUploadCommunityImage from 'services/Community/useUploadCommunityImage';
import useGetCommunityTags from 'services/Community/useGetCommunityTags';
import useGetCommunityCategories from 'services/Community/useGetCommunityCategories';
import { CreateIdeaParams, CreateStrategyParams } from 'types/ApiServicesTypes';
import MUIRichTextEditor from 'mui-rte';
import { ThemeProvider } from '@mui/styles';
import { stateToHTML } from 'draft-js-export-html';
import { convertFromHTML, ContentState, convertToRaw, RawDraftContentState } from 'draft-js';
import { createTheme } from '@mui/material/styles';
import CustomScrollbar from '../AiBots/CustomScrollbar';

interface ContentBlock {
  id: string;
  type: 'text' | 'image';
  content: string; // This will store the HTML content
  image_url?: string;
  image_alt?: string;
  sequence: number;
  richTextState?: RawDraftContentState; // Store the rich text editor state
  collapsed?: boolean; // Track if the block is collapsed
}

interface CreatePostData {
  title: string;
  images: string[];
  type: 'ideas' | 'strategies';
  content_blocks: ContentBlock[]; // Keep as array but will contain HTML
  tag_ids: number[];
  category_id?: number;
}

interface CreatePostModalProps {
  onClose: () => void;
  onCreate: (post: CreatePostData) => void;
  defaultType?: 'ideas' | 'strategies';
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({ 
  onClose, 
  onCreate, 
  defaultType = 'ideas' 
}) => {
  const theme = useTheme();
  const [title, setTitle] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [type, setType] = useState<'ideas' | 'strategies'>(defaultType);
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined);
  const [errors, setErrors] = useState<string[]>([]);

  const uploadImageMutation = useUploadCommunityImage();
  const tagsQuery = useGetCommunityTags();
  const categoriesQuery = useGetCommunityCategories();

  const addContentBlock = () => {
    const newBlock: ContentBlock = {
      id: Date.now().toString(),
      type: 'text',
      content: '',
      sequence: contentBlocks.length,
      richTextState: convertToRaw(ContentState.createFromText('')), // Initialize with empty rich text state
      collapsed: false // Initialize as expanded
    };
    setContentBlocks([...contentBlocks, newBlock]);
  };

  const removeContentBlock = (id: string) => {
    setContentBlocks(contentBlocks.filter(block => block.id !== id));
  };

  const updateContentBlock = (id: string, updates: Partial<ContentBlock>) => {
    setContentBlocks(blocks =>
      blocks.map(block =>
        block.id === id ? { ...block, ...updates } : block
      )
    );
  };

  const handleImageUpload = async (file: File, blockId: string) => {
    try {
      const result = await uploadImageMutation.mutateAsync(file);
      if (result.success) {
        updateContentBlock(blockId, {
          image_url: result.data.url,
          image_alt: file.name
        });
      }
    } catch (error) {
      setErrors(prev => [...prev, 'Failed to upload image']);
    }
  };

  const handleImageChange = async(e: React.ChangeEvent<HTMLInputElement>, blockId: string) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      await handleImageUpload(file, blockId);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!title.trim()) {
      newErrors.push('Title is required');
    }

    if (selectedTags.length === 0) {
      newErrors.push('Please select at least one tag');
    }

    if (!selectedCategory) {
      newErrors.push('Please select a category');
    }

    // Validate content blocks
    contentBlocks.forEach((block, index) => {
      if (block.type === 'text') {
        // For rich text, check if there's actual content (not just empty paragraphs)
        const hasContent = block.content && 
          block.content.replace(/<p><\/p>/g, '').replace(/<p>\s*<\/p>/g, '').trim() !== '';
        if (!hasContent) {
          newErrors.push(`Text block ${index + 1} cannot be empty`);
        }
      }
      if (block.type === 'image' && !block.image_url) {
        newErrors.push(`Image block ${index + 1} must have an image`);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // Convert content blocks to HTML array
  const convertContentBlocksToHtmlArray = (): ContentBlock[] => {
    if (contentBlocks.length === 0) {
      return [];
    }

    return contentBlocks.map((block, index) => {
      if (block.type === 'text') {
        // For text blocks, the content is already HTML from the rich text editor
        return {
          ...block,
          content: block.content || '<p></p>', // Ensure there's always some HTML content
          sequence: index
        };
      } else if (block.type === 'image' && block.image_url) {
        // Convert image to HTML img tag with responsive styling
        const altText = block.image_alt || 'Image';
        const imageHtml = `
          <div style="margin: 16px 0; text-align: center;">
            <img 
              src="${block.image_url}" 
              alt="${altText}" 
              style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);" 
            />
            ${block.image_alt ? `<p style="margin: 8px 0 0 0; font-size: 14px; color: #666; font-style: italic;">${block.image_alt}</p>` : ''}
          </div>
        `;
        
        return {
          ...block,
          content: imageHtml,
          sequence: index
        };
      }
      
      return {
        ...block,
        sequence: index
      };
    });
  };

  // Helper function to get combined HTML for preview
  const getCombinedHtmlForPreview = (): string => {
    const htmlBlocks = convertContentBlocksToHtmlArray();
    const htmlContent = htmlBlocks.map(block => block.content).join('');
    return `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6;">${htmlContent}</div>`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Convert content blocks to HTML array
    const htmlContentBlocks = convertContentBlocksToHtmlArray();

    const newPost: CreatePostData = {
      title,
      images,
      type,
      content_blocks: htmlContentBlocks, // Send array of HTML blocks
      tag_ids: selectedTags,
      category_id: selectedCategory
    };

    onCreate(newPost);
  };

  const renderContentBlock = (block: ContentBlock, index: number) => {
    const myTheme = createTheme({});
    
    Object.assign(myTheme, {
      overrides: {
        MUIRichTextEditor: {
          root: {
            minHeight: '200px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            marginTop: '8px',
          },
          editor: {
            paddingLeft: '14px',
            paddingRight: '14px',
          },
          placeHolder: {
            paddingLeft: '14px',
          },
        },
      },
    });

    return (
      <Card key={block.id} sx={{ mb: 2, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              size="small"
              onClick={() => updateContentBlock(block.id, { collapsed: !block.collapsed })}
              sx={{ 
                mr: 1, 
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              {block.collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              Block {index + 1} ({block.type})
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={block.type}
                label="Type"
                onChange={(e) => updateContentBlock(block.id, { type: e.target.value as 'text' | 'image' })}
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="image">Image</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              size="small"
              color="error"
              onClick={() => removeContentBlock(block.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          {!block.collapsed && (
            <Box sx={{
              transition: theme.transitions.create(['opacity', 'max-height'], {
                duration: theme.transitions.duration.standard,
              }),
              overflow: 'hidden',
            }}>
              {block.type === 'text' ? (
                <ThemeProvider theme={myTheme}>
                  <MUIRichTextEditor
                    controls={[
                      'title',
                      'paragraph',
                      'list',
                      'bold',
                      'italic',
                      'underline',
                      'highlight',
                      'link',
                      'numberList',
                      'bulletList',
                      'quote',
                      'undo',
                      'redo',
                    ]}
                    onSave={value => {
                      const htmlContent = stateToHTML(ContentState.createFromText(value));
                      updateContentBlock(block.id, {
                        content: htmlContent,
                        richTextState: convertToRaw(ContentState.createFromText(htmlContent))
                      });
                    }}
                    onChange={value => {
                      const htmlContent = stateToHTML(value.getCurrentContent());
                      updateContentBlock(block.id, {
                        content: htmlContent
                      });
                    }}
                    defaultValue={block.richTextState ? JSON.stringify(block.richTextState) : JSON.stringify(convertToRaw(ContentState.createFromText(block.content || '')))}
                    inlineToolbar
                    label="Content"
                  />
                </ThemeProvider>
              ) : (
                <Box>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, block.id)}
                    style={{ marginBottom: 8 }}
                  />
                  {block.image_url && (
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={block.image_url}
                        alt={block.image_alt || 'Uploaded image'}
                        style={{
                          maxWidth: '100%',
                          maxHeight: 200,
                          objectFit: 'contain',
                          borderRadius: theme.shape.borderRadius
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Image Alt Text"
                        placeholder="Describe the image..."
                        value={block.image_alt || ''}
                        onChange={(e) => updateContentBlock(block.id, { image_alt: e.target.value })}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
          
          {/* Show collapsed indicator */}
          {block.collapsed && (
            <Box sx={{ 
              p: 1, 
              backgroundColor: theme.palette.grey[50], 
              borderRadius: theme.shape.borderRadius,
              border: `1px dashed ${theme.palette.divider}`,
              textAlign: 'center'
            }}>
              <Typography variant="caption" color="text.secondary">
                {block.type === 'text' 
                  ? block.content ? 'Text content (click to expand)' : 'Empty text block'
                  : block.image_url ? 'Image uploaded (click to expand)' : 'No image uploaded'
                }
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <CustomScrollbar>

    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: `rgba(0,0,0,${theme.palette.mode === 'dark' ? '0.7' : '0.5'})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <Box component="form" onSubmit={handleSubmit} sx={{
        background: theme.palette.background.paper,
        // borderRadius: theme.shape.borderRadius * 2,
        padding: 3,
        width: '90vw',
        maxWidth: 800,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: theme.shadows[8]
      }}>
        <Typography variant="h5" sx={{ marginBottom: 2 }}>
          Create {type === 'ideas' ? 'Idea' : 'Strategy'}
        </Typography>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}
        
        {/* API Loading States */}
        {(tagsQuery.isLoading || categoriesQuery.isLoading) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Loading tags and categories...
          </Alert>
        )}
        
        {(tagsQuery.isError || categoriesQuery.isError) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Failed to load some data. Please refresh and try again.
          </Alert>
        )}
        
        {/* Type Selection */}
        <Box sx={{ mb: 3 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Post Type</FormLabel>
            <RadioGroup
              row
              value={type}
              onChange={(e) => setType(e.target.value as 'ideas' | 'strategies')}
            >
              <FormControlLabel value="ideas" control={<Radio />} label="Ideas" />
              <FormControlLabel value="strategies" control={<Radio />} label="Strategies" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Title"
            placeholder="Enter title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          
          {/* Category Selection */}
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory || ''}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value as number || undefined)}
            >
              <MenuItem value="">
                <em>Select a category</em>
              </MenuItem>
              {categoriesQuery.data?.data?.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Tags Selection */}
          <FormControl fullWidth>
            <InputLabel>Tags</InputLabel>
            <Select
              multiple
              value={selectedTags}
              onChange={(e) => setSelectedTags(e.target.value as number[])}
              input={<OutlinedInput label="Tags" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((tagId) => {
                    const tag = tagsQuery.data?.data?.find(t => t.id === tagId);
                    return (
                      <Chip 
                        key={tagId} 
                        label={tag?.name || `Tag ${tagId}`} 
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {tagsQuery.data?.data?.map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  <Checkbox checked={selectedTags.indexOf(tag.id) > -1} />
                  <ListItemText primary={tag.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Content Blocks Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Content Blocks</Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addContentBlock}
              size="small"
            >
              Add Block
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add multiple text and image blocks to create rich content
          </Typography>

          {/* Text Formatting Tips */}
          <Box sx={{ 
            p: 2, 
            backgroundColor: theme.palette.info.light, 
            borderRadius: theme.shape.borderRadius,
            mb: 2
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.info.dark }}>
              💡 Rich Text Editor Features:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.info.dark, fontSize: '12px' }}>
              • Use the <strong>Title</strong> button to create heading(H1)<br/>
              • <strong>Bold</strong>, <em>italic</em>, <u>underline</u> text<br/>
              • Create lists and quotes<br/>
              • Add links and highlights<br/>
              • Undo/redo changes
            </Typography>
          </Box>

          {contentBlocks.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4, 
              border: `2px dashed ${theme.palette.divider}`, 
              borderRadius: theme.shape.borderRadius,
              color: theme.palette.text.secondary
            }}>
              <Typography>No content blocks added yet</Typography>
              <Typography variant="body2">Click `&quot;Add Block&quot;` to get started</Typography>
            </Box>
          ) : (
            contentBlocks.map((block, index) => renderContentBlock(block, index))
          )}
        </Box>

        {/* Legacy Images Section */}
        {/* <Divider sx={{ my: 3 }} />
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Additional Images (Legacy)</Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files) {
                const files = Array.from(e.target.files);
                const urls = files.map(file => URL.createObjectURL(file));
                setImages(urls);
              }
            }}
          />
          {images.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              {images.map((img, idx) => (
                <img 
                  key={idx} 
                  src={img} 
                  alt="preview" 
                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} 
                />
              ))}
            </Box>
          )}
        </Box> */}
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            type="submit"
            disabled={uploadImageMutation.isLoading || tagsQuery.isLoading || categoriesQuery.isLoading}
          >
            {uploadImageMutation.isLoading ? 'Uploading...' : 'Create Post'}
          </Button>
        </Box>
      </Box>
    </Box>
    </CustomScrollbar>
  );
};

export default CreatePostModal; 